import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { FiUserSignInSettings } from '@fi-app/user/user-edit/fi-user-sign-in-settings';
import { takeUntil } from 'rxjs/operators';
import { isDefined } from '@fi-shared/utils';
import { IFiUniqueContactData } from '@fi-shared/fi-directives/fi-custom-validators/fi-unique-contact-async-validator.directive';
import { FormControl, NgModel } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { FiCustomValidatorPatterns } from '@fi-shared/fi-custom-validator-patterns/fi-custom-validator-patterns';
import { FiUserSignInType } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiComponentsContainer } from '@fi-shared/fi-components-container';
import { IContactModel } from '@fi-shared/models/common';

@Component({
    selector: 'fi-user-sign-in-type',
    templateUrl: './fi-user-sign-in-type.component.html',
    styleUrls: ['./fi-user-sign-in-type.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    providers: [{ provide: FiComponentsContainer, useExisting: FiUserSignInTypeComponent }],
    host: { class: 'fi-user-sign-in-type' },
    standalone: false,
})
export class FiUserSignInTypeComponent extends FiComponentsContainer implements OnInit {
    @Input() fiUserSignInSettings: FiUserSignInSettings;
    @Input() userNameLabel: string;
    @Input() isLoginInputDisabled: boolean;
    @Input() userId: number;
    @Input() phoneNumberContacts: IContactModel[] = [];
    @Input() emailContacts: IContactModel[] = [];

    private loginNameStatusChangeSubscription: Subscription;
    private loginNameModelControl: FormControl;
    @ViewChild('loginName') set _loginNameModel(value: NgModel) {
        if (isDefined(value)) {
            this.loginNameModelControl = value.control;
            this.subscribeOnControlStatusChange();
        } else {
            this.loginNameStatusChangeSubscription?.unsubscribe();
        }
    }

    constructor(private cdr: ChangeDetectorRef) {
        super();
    }

    ngOnInit() {
        this.subscribeOnSignInEmailContactChange();
    }

    private subscribeOnControlStatusChange() {
        this.loginNameStatusChangeSubscription = this.loginNameModelControl.statusChanges
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(() => this.cdr.markForCheck());
    }

    private subscribeOnSignInEmailContactChange() {
        this.fiUserSignInSettings.updateSignInInputValidity$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
            if (isDefined(this.loginNameModelControl)) {
                this.updateLoginContactInputValidity();
            }
            this.cdr.markForCheck();
        });
    }

    getDataForUniqueContactCheck(): IFiUniqueContactData {
        return {
            userId: this.userId,
            doCheck: this.fiUserSignInSettings.doContactChecks(),
            throwOnlyWhenContactUsedAsLogin: false,
            isSignIn: true,
        };
    }

    private updateLoginContactInputValidity() {
        setTimeout(() => {
            this.loginNameModelControl.markAsTouched();
            this.loginNameModelControl.updateValueAndValidity();
        });
    }

    getValidationPattern(): string {
        // system users can have any login name (without spaces)
        return !this.fiUserSignInSettings.allowCustomSignIn() ? FiCustomValidatorPatterns.Email : '[^\\s]+';
    }

    getPatternErrorLabel(): string {
        return this.fiUserSignInSettings.doContactChecks()
            ? 'error_message.email_invalid'
            : 'error_message.login_name_wrong_characters';
    }

    private ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    protected readonly FiUserSignInType = FiUserSignInType;
}
